import React, { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import URI from 'urijs'

import {
  useQueryString,
  useLiveRealtimeQuery,
} from '../../../sharedLegacy/hooks'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'

import {
  JoinToSpeakRole,
  JoinToSpeakProvider,
} from '../../../sharedLegacy/join-to-speak'
import { SimplePubSubClientProvider } from '../../../sharedLegacy/providers/simple-pub-sub-client-provider'
import WebinarRoom from '../WebinarContainerComponent/helpers/WebinarRoom'
import WebinarFoyer from '../WebinarFoyerComponent/WebinarFoyerComponent'
import {
  WebinarHostOptions,
  WebinarSpeakerOptions,
} from './WebinarFoyerOptionsComponent'
import WebinarConnectingComponent from '../WebinarConnectingComponent/WebinarConnectingComponent'

import useWebinarAppState from '../../providers/WebinarAppStateProvider'
import { useConnectInfoQuery } from '../../../global/blocs/useConnectInfo'
import { useRoomGlobalInfo } from '../../../global/blocs/roomInfo'

const WebinarAppComponent = () => {
  const { liveId, presenterToken } = useRoomGlobalInfo()
  const {
    setScope,
    scope,
    publicChannelURL,
    privateChannelURL,
    setPublicChannelURL,
    setPrivateChannelURL,
  } = useWebinarAppState()

  const [fastForwardQueryParam] = useQueryString('ff')
  const { data: webinarConnectInfo } = useConnectInfoQuery()
  const token = webinarConnectInfo?.token
  const location = useLocation()
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const { HasJoinToSpeakEnabled } = useFeatures()
  const { data: realtime } = useLiveRealtimeQuery({
    liveId,
    presenterToken,
    includePrivate: true,
  })
  const attendeeId = webinarConnectInfo?.live_speaker_id

  const { pathname } = location

  /**
   * When everything we needs is loaded, we hide the loader.
   */
  useEffect(() => {
    if (
      webinarConnectInfo &&
      publicChannelURL !== null &&
      privateChannelURL !== null
    ) {
      setIsDataLoaded(true)
    }
  }, [
    webinarConnectInfo,
    realtime,
    HasJoinToSpeakEnabled,
    publicChannelURL,
    privateChannelURL,
  ])

  /**
   * The url shouldn't be the desiding factor here, but its a quick way to
   *  get a good guess about the intended role for the user and because it's
   *  difficult to change the bootstrapping mechanics this time of writing.
   *
   * The tradeoff is this small hack, that should be cleaned up when we can
   *  get a clear way for the connect call to tell us the intended role of the
   *  user.
   */
  useEffect(() => {
    const [, role] = /webinar\/(presenter|producer)/.exec(pathname)
    setScope(role)
  }, [pathname, setScope])

  useEffect(() => {
    if (realtime !== undefined) {
      setPublicChannelURL(realtime.publicChannelURL)
      setPrivateChannelURL(realtime.privateChannelURL)
    }
  }, [realtime, setPublicChannelURL, setPrivateChannelURL])

  const onOptionClick = useCallback(
    (presenterLink) => (option) => () => {
      /**
       * If the intended remote role is equal to the one selected in the
       *  options, we set the scope of the application to that role.
       */
      if (scope === option) {
        const redirectUrl = new URI(window.location.href)
        redirectUrl.setSearch('ff', 1)
        window.location.replace(redirectUrl)
        return
      }

      /**
       * If the intended remote role was producer, but the option selected
       *  was presenter, then we as of right now want to redirect the user
       *  to a presenter link. This could be obsolete if we had the app-
       *  lication on a single url for both producer and presenters.
       */
      if (scope === 'producer' && option === 'presenter') {
        const redirectUrl = new URI(presenterLink)
        redirectUrl.setSearch('ff', 1)
        window.location.replace(redirectUrl)
      }
    },
    [scope]
  )

  if (!isDataLoaded)
    return <WebinarConnectingComponent isModalOpen loadingText="" />

  const isGearMode = webinarConnectInfo?.streaming_method === 'hardware'

  /**
   * If a scope hasn't been set for the application, we render the foyer.
   */
  if (!fastForwardQueryParam && !isGearMode) {
    return (
      <WebinarFoyer
        renderOptions={(role, presenterLink) => {
          if (role === 'producer') {
            return (
              <WebinarHostOptions
                onOptionClick={onOptionClick(presenterLink)}
              />
            )
          }

          if (role === 'presenter') {
            return (
              <WebinarSpeakerOptions
                onOptionClick={onOptionClick(presenterLink)}
              />
            )
          }

          return undefined
        }}
      />
    )
  }

  if (!realtime?.realtimeURL) {
    return null
  }

  const joinToSpeakRole =
    {
      producer: JoinToSpeakRole.Host,
      presenter: JoinToSpeakRole.Speaker,
    }[scope] ?? null

  /**
   * Render the webinar room
   */
  return (
    <SimplePubSubClientProvider url={realtime.realtimeURL}>
      <JoinToSpeakProvider
        role={joinToSpeakRole}
        isEnabled={HasJoinToSpeakEnabled}
        {...(joinToSpeakRole === JoinToSpeakRole.Host && {
          sessionId: liveId,
          token,
        })}
        {...(joinToSpeakRole === JoinToSpeakRole.Speaker && {
          sessionId: liveId,
          attendeeId,
        })}
      >
        <WebinarRoom />
      </JoinToSpeakProvider>
    </SimplePubSubClientProvider>
  )
}

export default WebinarAppComponent
